import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { LuCircleAlert, LuCircleCheck, LuX } from 'react-icons/lu';
import { useStrings } from '../tenant/TenantProvider';

const ToastContext = createContext(null);
const VISIBLE_MS = 4500;

export function ToastProvider({ children }) {
  const [toast, setToast] = useState(null);
  const timer = useRef(null);
  const t = useStrings();

  const dismiss = useCallback(() => {
    clearTimeout(timer.current);
    setToast(null);
  }, []);

  const show = useCallback((message, tone = 'success') => {
    clearTimeout(timer.current);
    setToast({ message, tone, id: Date.now() });
    timer.current = setTimeout(() => setToast(null), VISIBLE_MS);
  }, []);

  useEffect(() => () => clearTimeout(timer.current), []);

  const value = useMemo(() => ({ show, dismiss }), [show, dismiss]);
  const Icon = toast?.tone === 'error' ? LuCircleAlert : LuCircleCheck;

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toast-region" aria-live="polite">
        {toast && (
          <div key={toast.id} className={`toast toast--${toast.tone}`} role="status">
            <Icon className="toast__icon" aria-hidden="true" />
            <span className="toast__message">{toast.message}</span>
            <button type="button" className="icon-btn icon-btn--sm" aria-label={t('common.dismiss')} onClick={dismiss}>
              <LuX aria-hidden="true" />
            </button>
          </div>
        )}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used inside ToastProvider');
  return context;
}
